import { useState, useRef, useImperativeHandle, forwardRef, ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X } from 'lucide-react';
import Modal from './Modal';

interface ImageUploadProps {
  label: string;
  onFilesChange: (files: File[]) => void;
  accept?: string;
}

interface ImageUploadHandle {
  clear: () => void;
}

const ImageUpload = forwardRef<ImageUploadHandle, ImageUploadProps>(function ImageUpload({ label, onFilesChange, accept = 'image/*' }, ref) {
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // expose imperative clear method to parent
  useImperativeHandle(ref, () => ({
    clear: () => {
      setImages([]);
      setPreviews([]);
      if (inputRef.current) inputRef.current.value = '';
      // notify parent that files are cleared
      onFilesChange([]);
    }
  }));

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const updated = [...images, ...files];
    
    // Generate previews for all files (existing + new)
    const newPreviews: string[] = [];
    let loadedCount = 0;
    const totalFiles = updated.length;

    updated.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result;
        if (typeof result === 'string') {
          newPreviews[index] = result;
          loadedCount++;
          
          // Update state when all previews are loaded
          if (loadedCount === totalFiles) {
            setPreviews(newPreviews);
          }
        }
      };
      reader.readAsDataURL(file);
    });

    setImages(updated);
    onFilesChange(updated);
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      onFilesChange(updated);
      return updated;
    });

    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Card className="border-2 border-dashed py-1">
        <CardContent className="p-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-xs">{label}</h4>
              {images.length > 0 && (
                <Badge variant="secondary" className="text-xs h-5">
                  {images.length}
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-center gap-2 rounded border border-dashed border-muted-foreground/30 p-2 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Click to upload
              </p>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                onChange={handleSelect}
                className="hidden"
              />
            </div>

            {images.length > 0 && (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreviewModal(true)}
                  className="flex-1 h-6 text-xs"
                >
                  View ({images.length})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setImages([]);
                    setPreviews([]);
                    if (inputRef.current) inputRef.current.value = '';
                    onFilesChange([]);
                  }}
                  className="h-6 text-xs"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={`${label} Preview`}
      >
        <div className="grid grid-cols-3 gap-2 mt-2">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`${label} ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-0 right-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeImage(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
});

export default ImageUpload;

